import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Truck, RefreshCw, CheckCircle2, XCircle, MessageSquare, ExternalLink, Eye, FileText, Download, Copy, Ban } from "lucide-react";
import { Navbar } from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/carrier-vendors`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_COLORS = {
  submitted: "bg-blue-100 text-blue-700", compliance_review: "bg-indigo-100 text-indigo-700",
  more_information_requested: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700",
  approved_conditionally: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700",
  incomplete: "bg-slate-100 text-slate-600", ready_to_submit: "bg-cyan-100 text-cyan-700",
  archived: "bg-slate-200 text-slate-500",
};

export default function CarrierApprovalsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeReason, setMergeReason] = useState("Duplicate registration");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const r = await axios.get(`${API}?${params.toString()}`, auth());
    setItems(r.data.items || []); setCounts(r.data.status_counts || {});
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    const r = await axios.get(`${API}/${id}`, auth());
    setSel(r.data);
  };

  const act = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/${sel.id}/${path}`, body, auth());
      await open(sel.id); await load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const viewDoc = async (docId) => {
    try {
      const r = await axios.get(`${API}/${sel.id}/documents/${docId}/file`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) { alert("Could not open document"); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const r = await axios.get(`${API}/${sel.id}/documents/${docId}/file`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { alert("Could not download document"); }
  };

  const doCancel = () => {
    const reason = prompt("Cancel this application — reason (e.g. duplicate, withdrawn):", "Duplicate registration");
    if (reason && reason.trim()) act("cancel", { reason: reason.trim() });
  };

  const doMerge = async () => {
    if (!mergeTarget) { alert("Pick a record to merge into"); return; }
    await act("merge", { target_id: mergeTarget, reason: mergeReason || "Duplicate" });
    setMergeOpen(false); setMergeTarget(""); setSel(null);
  };

  const prettify = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const KV = ({ obj }) => (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 px-3 pb-3 sm:grid-cols-2">
      {Object.entries(obj).filter(([, v]) => v !== null && v !== "" && v !== undefined).map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1 text-xs">
          <dt className="text-slate-400">{prettify(k)}</dt>
          <dd className="text-right font-medium text-slate-700">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
  const SectionBody = ({ value }) => {
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2 px-3 pb-3">
          {value.map((item, i) => (
            <div key={i} className="rounded-md bg-slate-50 p-2">
              {typeof item === "object" && item !== null ? <KV obj={item} /> : <span className="text-xs text-slate-600">{String(item)}</span>}
            </div>
          ))}
        </div>
      );
    }
    if (typeof value === "object" && value !== null) return <KV obj={value} />;
    return <div className="px-3 pb-3 text-xs text-slate-600">{String(value)}</div>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="carrier-approvals-page">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-6 pt-24">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-orange-600 p-2 text-white"><Truck size={22} /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Carrier Approvals</h1>
              <p className="text-sm text-slate-500">Truck vendor registration review & approval</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/carrier-registration" target="_blank" rel="noreferrer"
              className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600">
              <ExternalLink size={14} /> Public form
            </a>
            <button data-testid="carrier-refresh" onClick={load} className="flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-white">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <button onClick={() => setStatus("")} className={`rounded-full px-3 py-1 text-xs ${status === "" ? "bg-orange-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>All</button>
          {Object.entries(counts).map(([s, n]) => (
            <button key={s} data-testid={`carrier-filter-${s}`} onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs ${status === s ? "bg-orange-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              {s.replace(/_/g, " ")} ({n})
            </button>
          ))}
          <input data-testid="carrier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / VAT / email"
            className="ml-auto rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-2">
            {items.length === 0 && <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-400">No applications</div>}
            {items.map((it) => (
              <button key={it.id} data-testid={`carrier-row-${it.id}`} onClick={() => open(it.id)}
                className={`w-full rounded-xl border bg-white p-3 text-left transition hover:shadow-sm ${sel?.id === it.id ? "border-orange-400" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{it.company?.registered_name || it.email}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_COLORS[it.status] || "bg-slate-100 text-slate-600"}`}>{it.status?.replace(/_/g, " ")}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{it.company?.vat_number || "no VAT"} · {it.email}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {!sel ? (
              <div className="rounded-xl bg-white p-8 text-center text-sm text-slate-400">Select an application to review</div>
            ) : (
              <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="carrier-detail">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-slate-800">{sel.company?.registered_name || sel.email}</h2>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[sel.status] || "bg-slate-100 text-slate-600"}`}>{sel.status?.replace(/_/g, " ")}</span>
                </div>
                {sel.carrier_ref && <p className="text-xs text-green-700">Carrier ref: {sel.carrier_ref}{sel.crm_company_id ? " · linked to CRM" : ""}</p>}

                {sel.auto_checks && (
                  <div className="mt-3 rounded-lg bg-slate-50 p-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Automatic checks</p>
                    {sel.auto_checks.map((c, i) => (
                      <div key={i} className={`text-xs ${c.pass ? "text-slate-600" : "text-amber-700"}`}>{c.pass ? "✅" : "⚠️"} {c.check.replace(/_/g, " ")}{c.note ? ` — ${c.note}` : ""}</div>
                    ))}
                  </div>
                )}

                {["company", "contacts", "licences", "insurance", "fleet", "routes", "cargo", "compliance", "banking"].map((k) => {
                  const v = sel[k];
                  if (!v || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && Object.keys(v).length === 0)) return null;
                  return (
                    <details key={k} className="mt-2 rounded-lg border border-slate-100" open={k === "contacts"}>
                      <summary className="cursor-pointer px-3 py-2 text-sm font-medium capitalize text-slate-700">{k}</summary>
                      <SectionBody value={v} />
                    </details>
                  );
                })}

                {sel.documents && sel.documents.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-slate-100 p-3" data-testid="carrier-documents">
                    <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700"><FileText size={15} /> Certificates & Documents ({sel.documents.length})</p>
                    <div className="space-y-1.5">
                      {sel.documents.map((d) => {
                        const expired = d.expiry_date && d.expiry_date < new Date().toISOString().slice(0, 10);
                        return (
                        <div key={d.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
                          <div className="min-w-0">
                            <span className="font-medium text-slate-700">{d.document_type || "Document"}</span>
                            {d.original_filename ? <span className="text-slate-500"> · {d.original_filename}</span> : null}
                            {d.expiry_date ? <span className={expired ? "font-semibold text-red-600" : "text-slate-400"}> · exp {d.expiry_date}{expired ? " (EXPIRED)" : ""}</span> : null}
                          </div>
                          {d.storage_path ? (
                            <div className="flex shrink-0 items-center gap-1.5">
                              <button data-testid={`carrier-doc-view-${d.id}`} onClick={() => viewDoc(d.id)}
                                className="flex items-center gap-1 rounded-md bg-slate-800 px-2.5 py-1 font-medium text-white hover:bg-slate-700">
                                <Eye size={12} /> View
                              </button>
                              <button data-testid={`carrier-doc-download-${d.id}`} onClick={() => downloadDoc(d.id, d.original_filename)}
                                className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-100">
                                <Download size={12} /> Download
                              </button>
                            </div>
                          ) : <span className="shrink-0 text-slate-400">metadata only</span>}
                        </div>
                      ); })}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500" data-testid="carrier-no-documents">
                    No certificate files uploaded by this carrier. Licence / insurance details (if any) are shown in the sections above; you can request the actual documents via "Request info".
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                  <button data-testid="carrier-approve-btn" disabled={busy}
                    onClick={() => { const c = prompt("Conditions (leave blank for full approval):", ""); act("approve", { conditions: c || null }); }}
                    className="flex items-center gap-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <CheckCircle2 size={15} /> Approve
                  </button>
                  <button data-testid="carrier-info-btn" disabled={busy}
                    onClick={() => { const m = prompt("What information do you need?"); if (m) act("request-info", { section: "company", message: m }); }}
                    className="flex items-center gap-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <MessageSquare size={15} /> Request info
                  </button>
                  <button data-testid="carrier-reject-btn" disabled={busy}
                    onClick={() => { const r = prompt("Rejection reason:"); if (r) act("reject", { reason: r }); }}
                    className="flex items-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    <XCircle size={15} /> Reject
                  </button>
                  <button data-testid="carrier-merge-btn" disabled={busy}
                    onClick={() => { setMergeReason("Duplicate registration"); setMergeTarget(""); setMergeOpen(true); }}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 hover:bg-slate-50">
                    <Copy size={15} /> Merge duplicate
                  </button>
                  <button data-testid="carrier-cancel-btn" disabled={busy}
                    onClick={doCancel}
                    className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 hover:bg-slate-50">
                    <Ban size={15} /> Cancel
                  </button>
                </div>

                {sel.merged_into && (
                  <p className="mt-2 text-xs text-slate-500">Merged into another record · {sel.cancellation_reason || ""}</p>
                )}
                {sel.status === "archived" && !sel.merged_into && sel.cancellation_reason && (
                  <p className="mt-2 text-xs text-slate-500">Cancelled — {sel.cancellation_reason}</p>
                )}

                {mergeOpen && (
                  <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid="carrier-merge-panel">
                    <p className="mb-2 text-sm font-semibold text-slate-700">Merge this duplicate into an existing carrier</p>
                    <p className="mb-2 text-xs text-slate-500">This record will be archived and linked to the record you choose below.</p>
                    <select data-testid="carrier-merge-target" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}
                      className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value="">— Select the record to keep —</option>
                      {items.filter((it) => it.id !== sel.id).map((it) => (
                        <option key={it.id} value={it.id}>{(it.company?.registered_name || it.email)}{it.company?.vat_number ? ` · ${it.company.vat_number}` : ""} ({it.status?.replace(/_/g, " ")})</option>
                      ))}
                    </select>
                    <input data-testid="carrier-merge-reason" value={mergeReason} onChange={(e) => setMergeReason(e.target.value)}
                      placeholder="Reason" className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button data-testid="carrier-merge-confirm" disabled={busy || !mergeTarget} onClick={doMerge}
                        className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">Confirm merge</button>
                      <button onClick={() => setMergeOpen(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600">Cancel</button>
                    </div>
                  </div>
                )}

                {sel.audit && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-400">Audit trail ({sel.audit.length})</summary>
                    <div className="mt-1 space-y-1">
                      {sel.audit.slice().reverse().map((a, i) => (
                        <div key={i} className="text-[11px] text-slate-500">{a.at?.slice(0, 16).replace("T", " ")} · {a.action} · {a.actor}{a.detail ? ` — ${a.detail}` : ""}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
